import React, {useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import styled from 'styled-components'
import {Spacing} from 'components/common/useBaseStyle'
import useHealthCenterHeartRateAnalysisStyles from 'pages/HealthCenter/ReportV1/HealthCenterHeartRateAnalysis/Style'
import {Divider, Typography} from '@material-ui/core'
import {HrvAutoNervousSystemBalance} from 'api/healthCareApi'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import {ResHrvAnalysis} from 'api/languageApi'

export const PageWrap = styled.div`
  background: white;
  width: 1280px;
  padding: 30px 60px;
`
export const PageContent = styled.div`
  width: 1160px;
`

export const PageCard = styled.div`
  width: 100%;
`
export const HeaderCard = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
`
export const HeaderCardNumber = styled.div`
  background-color: #2e4d6f;
  color: white;
  background-color: #2e4d6f;
  width: 42px;
  height: 42px;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0px 10px;
`

export const HeaderCardTitle = styled.div`
  font-size: 20px;
  height: 42px;
  display: flex;
  align-items: center;
  padding-left: 15px;
`

export const HeaderCardTitleGreen = styled.div`
  font-size: 18px;
  height: 40px;
  display: flex;
  align-items: center;
  border-radius: 10px 10px 0px 0px;
  background-color: #00a29c;
  color: white;
  padding: 10px 24px 12px;
  font-weight: 500;
  size: 18px;
  line-height: 18px;
`

export const CardContent = styled.div`
  font-size: 14px;
`

export const Line = styled.div`
  height: 1px;
  background-color: #2e4d6f;
  width: 1150px;
`

export const LineGreen = styled.div`
  height: 1px;
  background-color: #00a29c;
  width: 1145px;
`
export const LineGreenHaft = styled.div`
  height: 1px;
  background-color: #00a29c;
  width: 545px;
`

export const EmptyBox = styled.div`
  width: 10px;
`

export const ReportCustomPadding = styled.div`
  padding: ${Spacing.report.reportCustomPadding}px;
`

export const FlexBox = styled.div`
  display: flex;
`

export interface HeartRateAnalysisProps {
  hrvTableImage: string
  hrvCircularImage: string
  hrvAutoNervousSystemBalance: HrvAutoNervousSystemBalance | null
  hrvContourImage: string
  hrvHistogramImage: string
  hrvCircularLevel: number
  hrvContourLevel: number
  hrvHistogramLevel: number
  textHRV: ResHrvAnalysis
  footer: string
}

function HealthCenterHeartRateAnalysisPage({
  hrvTableImage,
  hrvCircularImage,
  hrvAutoNervousSystemBalance,
  hrvContourImage,
  hrvHistogramImage,
  hrvCircularLevel,
  hrvContourLevel,
  hrvHistogramLevel,
  textHRV,
  footer,
}: HeartRateAnalysisProps) {
  const {t} = useTranslation()
  const classes = useHealthCenterHeartRateAnalysisStyles()

  const automatic = textHRV.section.automatic_nervous_balance
  const dimensional = textHRV.section.two_dimensional_analysis
  const histogram = textHRV.section.histogram_analysis
  const CIRCULAR = {good: 1, normal: 3, bad: 2}
  const HISTOGRAM_LEVER = {good: 1, normal: 2, bad: 3}
  const APPEARS = {
    great: 3,
    good: 4,
    average: 7,
    fast: 6, // CONFIRM
    slow: 2, // CONFIRM
    overload: 1,
    fatigue: 8,
    weakness: 5,
  }
  const formatText = (template: string, values: (string | number)[]) => {
    return template.replace(/{(\d+)}/g, (_, index) => values[+index].toString())
  }
  const getHeaderCircular = (value: number) => {
    const parasympathetic = hrvAutoNervousSystemBalance?.parasympathetic ?? 0
    const sympathetic = hrvAutoNervousSystemBalance?.sympathetic ?? 0
    if (value === CIRCULAR.normal)
      return formatText(automatic.analysis_results.opinions.stage3.main, [
        parasympathetic,
        sympathetic,
      ])
    if (value === CIRCULAR.bad)
      return formatText(automatic.analysis_results.opinions.stage2.main, [
        parasympathetic,
        sympathetic,
      ])
    return formatText(automatic.analysis_results.opinions.stage1.main, [
      parasympathetic,
      sympathetic,
    ])
  }

  const getContentCircular = (value: number) => {
    if (value === CIRCULAR.good) return t('ISymptomsSuchGood')
    if (value === CIRCULAR.normal) return t('ISymptomsSuchNormal')
    return t('ISymptomsSuchBad')
  }

  const getHeaderAppears = (value: number) => {
    const data = dimensional.analysis_results.opinions
    if (value === APPEARS.great) return data.stage3.main // 3
    if (value === APPEARS.good) return data.stage4.main // 4
    if (value === APPEARS.average) return data.stage7.main // 7
    if (value === APPEARS.fast) return data.stage6.main // 6 -
    if (value === APPEARS.slow) return data.stage2.main // 2 -
    if (value === APPEARS.overload) return data.stage1.main // 1
    if (value === APPEARS.fatigue) return data.stage8.main // 8
    return data.stage5.main // 5
  }

  const getContentAppears = (value: number) => {
    const data = dimensional.analysis_results.opinions
    if (value === APPEARS.great) return [data.stage3.sub]
    if (value === APPEARS.good) return [data.stage4.sub]
    if (value === APPEARS.average) return [data.stage7.sub]
    if (value === APPEARS.fast) return [data.stage6.sub]
    if (value === APPEARS.slow) return [data.stage2.sub]
    if (value === APPEARS.overload) return [data.stage1.sub]
    if (value === APPEARS.fatigue) return [data.stage8.sub]
    return [data.stage5.sub]
  }

  const getHeaderHistogramLevel = (value: number) => {
    const data = histogram.analysis_results.opinions
    if (value === HISTOGRAM_LEVER.good) return data.stage1.main
    if (value === HISTOGRAM_LEVER.normal) return data.stage2.main
    return data.stage3.main
  }

  const getContentHistogramLevel = (value: number) => {
    const data = histogram.analysis_results.opinions
    if (value === HISTOGRAM_LEVER.good) return data.stage1.sub
    if (value === HISTOGRAM_LEVER.normal) return data.stage2.sub
    return data.stage3.sub
  }
  const ref = useRef<HTMLDivElement>(null)
  const ref1 = useRef<HTMLDivElement>(null)
  const ref2 = useRef<HTMLDivElement>(null)
  const ref3 = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML =
        automatic.pie_chart.annotation.parasympathetic.case
    }
    if (ref1.current) {
      ref1.current.innerHTML =
        automatic.pie_chart.annotation.parasympathetic.symptoms
    }
    if (ref2.current) {
      ref2.current.innerHTML = automatic.pie_chart.annotation.sympathetic.case
    }
    if (ref3.current) {
      ref3.current.innerHTML =
        automatic.pie_chart.annotation.sympathetic.symptoms
    }
  }, [textHRV])
  return (
    <div className={classes.heartRate}>
      <PageWrap>
        <PageContent>
          <WrapElement number={1} text={automatic.title}>
            <FlexBox>
              <CardContent>
                <div className={classes.paddingContent}>
                  <p className={classes.fontWeightContent}>
                    {automatic.description.main}
                  </p>
                  <p className={classes.variabilityLabel}>
                    {automatic.description.sub}
                  </p>
                </div>
                <div className={classes.contentCard}>
                  <div className={classes.tablePercent}>
                    <img src={hrvTableImage} alt='Table' />
                  </div>
                  <div className={classes.circularChart}>
                    <div className={classes.flexCircular}>
                      <div className={classes.changePosition}>
                        <div className={classes.textCircularTop}>
                          {automatic.pie_chart.title}
                        </div>
                        <div>
                          <img
                            className={classes.imgCircular}
                            src={hrvCircularImage}
                            alt='Circular'
                          />
                        </div>
                      </div>
                    </div>
                    <div className={classes.textEndCircular}>
                      <div className={classes.textLeftCircular}>
                        <div>
                          <div className={classes.textLeftGreen} ref={ref} />

                          <div className={classes.textLeftDec} ref={ref1} />
                        </div>
                      </div>
                      <div className={classes.textRightCircular}>
                        <div style={{width: '150px'}}>
                          <div className={classes.textRightGreen} ref={ref2} />

                          <div className={classes.textRightDec} ref={ref3} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Typography className={classes.padding} />
                <Typography className={classes.padding} />
                <div className={classes.paddingContent}>
                  <HeaderCard>
                    <HeaderCardTitleGreen>
                      {automatic.analysis_results.title}
                    </HeaderCardTitleGreen>
                  </HeaderCard>
                  <FlexBox>
                    <LineGreen> </LineGreen>
                  </FlexBox>
                  <div className={classes.contentAnalysis}>
                    <p className={classes.fontWeightContent}>
                      {getHeaderCircular(hrvCircularLevel)}
                    </p>
                    <p className={classes.fontSize18}>
                      {getContentCircular(hrvCircularLevel)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </FlexBox>
          </WrapElement>
          <WrapElement number={2} text={dimensional.title}>
            <div className={classes.contentCard}>
              <div className={classes.tablePercentMargin}>
                <img src={hrvContourImage} alt='Contour' />
              </div>
              <div className={classes.pieChart}>
                <div>
                  <div>
                    <p className={classes.variabilityLabel}>
                      {dimensional.description}
                    </p>
                  </div>
                  <Typography className={classes.padding} />
                  <Typography className={classes.padding} />
                  <div>
                    <HeaderCard>
                      <HeaderCardTitleGreen>
                        {dimensional.analysis_results.title}
                      </HeaderCardTitleGreen>
                    </HeaderCard>
                    <FlexBox>
                      <LineGreenHaft> </LineGreenHaft>
                    </FlexBox>
                    <div className={classes.contentAnalysis}>
                      <p
                        className={classes.fontWeightContentHeaderAppears}
                        style={{
                          fontSize: '19px !important',
                          letterSpacing: '0.28px !important',
                        }}
                      >
                        {getHeaderAppears(hrvContourLevel)}
                      </p>
                      {getContentAppears(hrvContourLevel).map((item) => (
                        <p className={classes.variabilityLabel} key={item}>
                          {item}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </WrapElement>
          <WrapElement number={3} text={histogram.title}>
            <div className={classes.contentCard}>
              <div className={classes.tablePercentMargin}>
                <img src={hrvHistogramImage} alt='Histogram' />
              </div>
              <div className={classes.pieChart}>
                <div>
                  <div>
                    <p className={classes.fontWeightContent}>
                      {histogram.description.main}
                    </p>
                    <p className={classes.variabilityLabel}>
                      {histogram.description.sub}
                    </p>
                  </div>
                  <Typography className={classes.padding} />
                  <Typography className={classes.padding} />
                  <div>
                    <HeaderCard>
                      <HeaderCardTitleGreen>
                        {histogram.analysis_results.title}
                      </HeaderCardTitleGreen>
                    </HeaderCard>
                    <FlexBox>
                      <LineGreenHaft> </LineGreenHaft>
                    </FlexBox>
                    <div className={classes.contentAnalysis}>
                      <p className={classes.fontWeightContent}>
                        {getHeaderHistogramLevel(hrvHistogramLevel)}
                      </p>
                      <p className={classes.variabilityLabel}>
                        {getContentHistogramLevel(hrvHistogramLevel)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </WrapElement>
          <Divider
            style={{marginLeft: 10, marginRight: 10, marginBottom: 10}}
          />
          <div className={classes.footer}>{footer}</div>
        </PageContent>
      </PageWrap>
    </div>
  )
}

export default HealthCenterHeartRateAnalysisPage
